import { useNavigate, useLocation } from 'react-router-dom';
import { BackButton } from '@/components/ui/app/back-button';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import i18n from '@/lib/i18n';

// Données d'exemple par défaut
function getDefaultPreviewData() {
    return {
        name: i18n.t('procedures:inspectionDeTravauxCee'),
        description:
            i18n.t('procedures:procedureCompletePourRealiserUneInspectionDeTrav'),
        color: '#10B981',
        sections: [
            {
                id: 1,
                name: i18n.t('procedures:informationsGenerales'),
                description: i18n.t('procedures:informationsDeBaseSurLesTravaux'),
                fields: [
                    { id: 1, type: 'text', label: i18n.t('procedures:typeDeTravaux'), required: true },
                    { id: 2, type: 'text', label: i18n.t('procedures:surfaceTraiteeM'), required: true },
                    { id: 3, type: 'date', label: i18n.t('procedures:dateDesTravaux'), required: true },
                ],
            },
            {
                id: 2,
                name: i18n.t('procedures:etatDesLieux'),
                description: i18n.t('procedures:photosAvantTravaux'),
                fields: [
                    {
                        id: 4,
                        type: 'photo',
                        label: i18n.t('procedures:photosAvantTravaux2'),
                        required: true,
                        instructions: i18n.t('procedures:prenezDesPhotosDeLEtatInitial'),
                    },
                ],
            },
            {
                id: 3,
                name: i18n.t('procedures:verificationsTechniques'),
                description: i18n.t('procedures:pointsDeControleObligatoires'),
                fields: [
                    {
                        id: 5,
                        type: 'checkbox',
                        label: i18n.t('procedures:pointsDeConformite'),
                        required: true,
                        options: ['Isolation conforme', i18n.t('procedures:etancheiteVerifiee'), 'Finitions OK'],
                    },
                    {
                        id: 6,
                        type: 'dropdown',
                        label: i18n.t('procedures:typeDIsolation'),
                        required: false,
                        options: ['Laine de verre', 'Laine de roche', i18n.t('procedures:polystyrene'), 'Autre'],
                    },
                    { id: 7, type: 'comment', label: i18n.t('procedures:observations'), required: false },
                ],
            },
        ],
    };
}

function getFieldTypeLabels() {
    return {
        text: i18n.t('procedures:champTexte'),
        checkbox: i18n.t('procedures:casesACocher'),
        dropdown: i18n.t('procedures:listeDeroulante'),
        comment: 'Commentaire',
        photo: i18n.t('procedures:photoCertifiee'),
        video: i18n.t('procedures:videoCertifiee'),
        date: 'Date',
    };
}

const FIELD_TYPE_ICONS = {
    text: '📝',
    checkbox: '☑️',
    dropdown: '📋',
    comment: '💬',
    photo: '📷',
    video: '🎥',
    date: '📅',
};

export default function ProcedurePreview() {
    const { t } = useTranslation();
    const FIELD_TYPE_LABELS = getFieldTypeLabels();
    const defaultPreviewData = getDefaultPreviewData();
    const navigate = useNavigate();
    const location = useLocation();

    // Récupérer les données passées via state ou utiliser les données par défaut
    const previewData = location.state?.procedureData || defaultPreviewData;
    const returnPath = location.state?.returnPath || '/procedures/create';

    const handleBack = () => {
        // Repasser les données au retour pour ne pas les perdre
        navigate(returnPath, {
            state: { procedureData: previewData },
        });
    };

    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-background">
                <div className="flex items-center gap-3">
                    <BackButton onClick={handleBack} />
                    <h1 className="text-lg font-semibold text-foreground">{t('procedures:apercuDeLaProcedure')}</h1>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 bg-surface">
                <div className="max-w-2xl mx-auto">
                    {/* Procedure Card Preview */}
                    <div className="bg-card rounded-xl border border-border overflow-hidden">
                        {/* Procedure Header */}
                        <div className="p-6 border-b border-border">
                            <div className="flex items-center gap-4 mb-4">
                                <div
                                    className="w-12 h-12 rounded-full flex items-center justify-center text-primary-foreground font-semibold text-lg"
                                    style={{ backgroundColor: previewData.color }}
                                >
                                    {previewData.name?.substring(0, 2).toUpperCase() || 'PR'}
                                </div>
                                <div>
                                    <h2 className="text-xl font-semibold text-foreground">
                                        {previewData.name || t('procedures:nomDeLaProcedure')}
                                    </h2>
                                    {previewData.description && (
                                        <p className="text-sm text-muted-foreground mt-1">{previewData.description}</p>
                                    )}
                                </div>
                            </div>
                            <div className="text-sm text-muted-foreground">
                                {previewData.sections?.length || 0} {t('procedures:sectionS')}{' '}
                                {previewData.sections?.reduce((acc, s) => acc + (s.fields?.length || 0), 0) || 0}{' '}
                                champ(s)
                            </div>
                        </div>

                        {/* Sections Preview */}
                        <div className="divide-y divide-border">
                            {previewData.sections?.map((section, sectionIndex) => (
                                <div key={section.id || sectionIndex} className="p-6">
                                    {/* Section Header */}
                                    <div className="flex items-center gap-3 mb-4">
                                        <div
                                            className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-medium text-primary-foreground"
                                            style={{ backgroundColor: previewData.color }}
                                        >
                                            {sectionIndex + 1}
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-semibold text-foreground">
                                                {section.name || `Section ${sectionIndex + 1}`}
                                            </h3>
                                            {section.description && (
                                                <p className="text-sm text-muted-foreground">{section.description}</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Fields Preview */}
                                    {section.fields?.length > 0 ? (
                                        <div className="space-y-3">
                                            {section.fields.map((field, fieldIndex) => (
                                                <div
                                                    key={field.id || fieldIndex}
                                                    className="px-4 py-3 bg-surface rounded-lg"
                                                >
                                                    <div className="flex items-start gap-3">
                                                        <span className="text-lg">
                                                            {FIELD_TYPE_ICONS[field.type] || '📝'}
                                                        </span>
                                                        <div className="flex-1">
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-sm font-medium text-foreground">
                                                                    {field.label || t('procedures:champSansNom')}
                                                                </span>
                                                                {field.required && (
                                                                    <span className="text-xs bg-destructive/10 text-destructive px-2 py-0.5 rounded">
                                                                        {t('procedures:obligatoire')}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="text-xs text-muted-foreground mt-1">
                                                                {FIELD_TYPE_LABELS[field.type] || field.type}
                                                            </div>

                                                            {/* Options preview for checkbox/dropdown */}
                                                            {(field.type === 'checkbox' || field.type === 'dropdown') &&
                                                                field.options?.length > 0 && (
                                                                    <div className="mt-2 flex flex-wrap gap-1">
                                                                        {field.options
                                                                            .filter((o) => o)
                                                                            .map((option, i) => (
                                                                                <span
                                                                                    key={i}
                                                                                    className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded"
                                                                                >
                                                                                    {option}
                                                                                </span>
                                                                            ))}
                                                                    </div>
                                                                )}

                                                            {/* Instructions for photo/video */}
                                                            {(field.type === 'photo' || field.type === 'video') && field.instructions && (
                                                                <div className="mt-2 text-xs text-muted-foreground italic">
                                                                    "{field.instructions}"
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-muted-foreground italic">
                                            {t('procedures:aucunChampDansCetteSection')}
                                        </p>
                                    )}
                                </div>
                            ))}

                            {(!previewData.sections || previewData.sections.length === 0) && (
                                <div className="p-6 text-center text-muted-foreground">{t('procedures:aucuneSectionDefinie')}</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-border bg-background">
                <div className="max-w-2xl mx-auto">
                    <Button
                        variant="soft"
                        color="secondary"
                        onClick={handleBack}
                        className="w-full px-4 py-3 rounded-lg"
                    >
                        {t('procedures:retourALEdition')}
                    </Button>
                </div>
            </div>
        </div>
    );
}
